import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2, ClipboardCopy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useWeb3Form } from "../../hooks/useWeb3Form";
import { generateBookingRef, SITE, detectArea, generateBookingKey } from "../../config/site";

const ukPhone = /^(?:\+44\s?|0)\d[\d\s]{8,12}$/;

const schema = z.object({
  direction: z.enum(["essex-to-london", "london-to-essex"]),
  pickup: z.string().trim().min(5, "Please enter the full pickup address."),
  destination: z.string().trim().min(5, "Please enter the full destination address."),
  date: z
    .string()
    .min(1, "Please choose a journey date.")
    .refine((v) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(v + "T00:00:00") >= today;
    }, "The journey date cannot be in the past."),
  time: z.string().min(1, "Please choose a preferred pickup time."),
  passengers: z.string().min(1, "Please select the number of passengers."),
  luggage: z.string().min(1, "Please tell us about your luggage."),
  name: z.string().trim().min(2, "Please enter your full name."),
  phone: z
    .string()
    .trim()
    .regex(ukPhone, "Please enter a valid UK mobile number (e.g. 07700 900000)."),
  email: z.string().trim().email("Please enter a valid email address."),
  notes: z.string().trim().optional(),
  privacy: z.literal(true, {
    errorMap: () => ({ message: "Please acknowledge the privacy notice to continue." }),
  }),
});

const inputCls =
  "w-full bg-transparent border border-[#27272A] rounded-sm p-3 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none";
const labelCls = "block text-sm uppercase tracking-[0.12em] font-semibold text-[#A1A1AA] mb-2";
const errCls = "mt-1.5 text-sm text-red-400";

function Field({ label, error, children, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelCls}>
        {label}
      </label>
      {children}
      {error && (
        <p role="alert" className={errCls} data-testid={`error-${htmlFor}`}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function BookingForm() {
  const bookingRef = useMemo(() => generateBookingRef(), []);
  const bookingKey = useMemo(() => generateBookingKey(), []);
  const { status, errorMessage, submit } = useWeb3Form({
    subject: "New £100 Journey Request",
    fromName: "Essex Black Cab website",
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { direction: "essex-to-london", passengers: "1", luggage: "Hand luggage only" },
  });

  const direction = watch("direction");
  const today = new Date().toISOString().split("T")[0];
  const [confirmation, setConfirmation] = useState(null);

  const onSubmit = async (values) => {
    // Privacy-safe, area-only route labels for the owner share link.
    const pickupSide = values.direction === "essex-to-london" ? "essex" : "london";
    const destSide = pickupSide === "essex" ? "london" : "essex";
    const fromArea = detectArea(values.pickup, pickupSide);
    const toArea = detectArea(values.destination, destSide);
    const shareUrl =
      `${window.location.origin}/job-share?` +
      new URLSearchParams({
        key: bookingKey,
        ref: bookingRef,
        from: fromArea,
        to: toArea,
        date: values.date,
        time: values.time,
        passengers: values.passengers,
        luggage: values.luggage,
      }).toString();

    // Passenger acknowledgement (sent via FormSubmit's free autoresponse —
    // the reference is embedded here, so it always matches the screen).
    const autoresponse = [
      "Thank you — your Essex Black Cab journey request has been received.",
      "",
      `Booking reference: ${bookingRef}`,
      `Journey: ${fromArea} → ${toArea}`,
      `Date: ${values.date} | Pickup time: ${values.time}`,
      "Fare: £100 fixed — pay your driver directly inside the cab, by cash or card, at the end of the journey. No advance payment or deposit.",
      "",
      "Your journey request has been received, but your booking is not yet confirmed. We will contact you once a driver has been arranged.",
      "",
      "Essex Black Cab — essexblackcab.co.uk",
    ].join("\n");

    const ok = await submit(
      {
        "Booking reference": bookingRef,
        "Journey direction":
          values.direction === "essex-to-london" ? "Essex → London" : "London → Essex",
        "Pickup address": values.pickup,
        "Destination address": values.destination,
        "Journey date": values.date,
        "Pickup time": values.time,
        "Passengers": values.passengers,
        "Luggage": values.luggage,
        "Passenger name": values.name,
        "Mobile number": values.phone,
        "Additional information": values.notes || "—",
        "Fixed fare": "£100",
        "MANAGE THIS BOOKING (owner only — do not forward)": shareUrl,
        email: values.email,
        replyto: values.email,
      },
      autoresponse
    );
    if (ok) {
      setConfirmation({
        fromArea,
        toArea,
        date: values.date,
        time: values.time,
      });
      // Store the booking for the owner's MANAGE THIS BOOKING prefill.
      // Independent of email delivery — failure here only means the link
      // falls back to manual entry.
      try {
        await fetch("/api/bookings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            key: bookingKey,
            ref: bookingRef,
            data: {
              "Journey direction":
                values.direction === "essex-to-london" ? "Essex → London" : "London → Essex",
              "Pickup address": values.pickup,
              "Destination address": values.destination,
              "Journey date": values.date,
              "Pickup time": values.time,
              "Passengers": values.passengers,
              "Luggage": values.luggage,
              "Passenger name": values.name,
              "Mobile number": values.phone,
              "Email address": values.email,
              "Additional information": values.notes || "—",
            },
          }),
        });
      } catch {
        // Non-fatal by design.
      }
    }
  };

  if (status === "success") {
    const copyRef = async () => {
      try {
        await navigator.clipboard.writeText(bookingRef);
        toast.success("Booking reference copied.");
      } catch {
        toast.error("Copy failed — please long-press the reference to copy it.");
      }
    };
    return (
      <div
        data-testid="booking-confirmation"
        className="border border-[rgba(212,175,55,0.4)] bg-[#111111] p-6 sm:p-8"
      >
        <CheckCircle2 className="h-10 w-10 text-[#D4AF37]" aria-hidden="true" />
        <h3 className="mt-4 text-2xl font-semibold text-white">
          Thank you — your journey request has been received!
        </h3>

        <dl className="mt-6 divide-y divide-[#27272A] border border-[#27272A] text-sm">
          <div className="flex items-center justify-between gap-4 p-4">
            <dt className="text-[#A1A1AA]">Booking reference</dt>
            <dd className="flex items-center gap-3">
              <span className="font-semibold text-[#D4AF37]" data-testid="booking-reference">
                {bookingRef}
              </span>
              <button
                type="button"
                onClick={copyRef}
                data-testid="copy-booking-reference-button"
                className="focus-gold inline-flex items-center gap-1.5 border border-[#D4AF37] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#D4AF37] transition-colors duration-150 hover:bg-[#D4AF37] hover:text-black"
              >
                <ClipboardCopy className="h-3.5 w-3.5" aria-hidden="true" /> Copy
              </button>
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 p-4">
            <dt className="text-[#A1A1AA]">Journey</dt>
            <dd className="text-right font-medium text-white" data-testid="confirmation-journey">
              {confirmation ? `${confirmation.fromArea} → ${confirmation.toArea}` : "—"}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 p-4">
            <dt className="text-[#A1A1AA]">Date</dt>
            <dd className="font-medium text-white" data-testid="confirmation-date">{confirmation?.date}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 p-4">
            <dt className="text-[#A1A1AA]">Pickup time</dt>
            <dd className="font-medium text-white" data-testid="confirmation-time">{confirmation?.time}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 p-4">
            <dt className="text-[#A1A1AA]">Fare</dt>
            <dd className="font-semibold text-[#D4AF37]">£100 fixed</dd>
          </div>
        </dl>

        <div
          className="mt-6 flex items-start gap-3 border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)] p-4"
          data-testid="confirmation-not-confirmed"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-[#D4D4D8]">
            <strong className="text-white">
              Your journey request has been received, but your booking is not yet confirmed.
            </strong>{" "}
            We will review your request, check driver availability and contact you to confirm
            your booking. Please do not assume a driver has been allocated until you receive
            confirmation from us.
          </p>
        </div>

        <p className="mt-5 text-sm leading-relaxed text-[#A1A1AA]">
          <strong className="text-white">Payment:</strong> £100 directly to your driver inside
          the cab, cash or card, at the end of the journey. No advance payment or deposit. A
          copy of this acknowledgement is on its way to your email inbox.
        </p>

        <p className="mt-5 border-t border-[#27272A] pt-4 text-sm leading-relaxed text-[#A1A1AA]">
          Need to change anything? Email{" "}
          <a
            href={`mailto:${SITE.email}?subject=Booking%20${bookingRef}`}
            data-testid="confirmation-email-link"
            className="focus-gold text-[#D4AF37] underline-offset-4 hover:underline"
          >
            {SITE.email}
          </a>{" "}
          quoting your reference.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      data-testid="booking-form"
      className="space-y-6"
    >
      {/* Honeypot — must stay hidden and empty */}
      <input type="checkbox" name="botcheck" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

      <div>
        <span className={labelCls} id="direction-label">Journey direction</span>
        <div role="group" aria-labelledby="direction-label" className="grid grid-cols-2 gap-3">
          {[
            { value: "essex-to-london", label: "Essex → London" },
            { value: "london-to-essex", label: "London → Essex" },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`direction-${opt.value}`}
              aria-pressed={direction === opt.value}
              onClick={() => setValue("direction", opt.value, { shouldValidate: true })}
              className={`focus-gold border p-3.5 text-sm font-semibold uppercase tracking-wider transition-colors duration-150 ${
                direction === opt.value
                  ? "border-[#D4AF37] bg-[rgba(212,175,55,0.15)] text-[#D4AF37]"
                  : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <Field label={direction === "essex-to-london" ? "Pickup address (Essex)" : "Pickup address (London)"} error={errors.pickup?.message} htmlFor="pickup">
        <input id="pickup" data-testid="input-pickup" className={inputCls} placeholder="Full pickup location" {...register("pickup")} />
      </Field>

      <Field label={direction === "essex-to-london" ? "Destination address (London)" : "Destination address (Essex)"} error={errors.destination?.message} htmlFor="destination">
        <input id="destination" data-testid="input-destination" className={inputCls} placeholder="Full drop-off location" {...register("destination")} />
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Journey date" error={errors.date?.message} htmlFor="date">
          <input id="date" data-testid="input-date" type="date" min={today} className={`${inputCls} [color-scheme:dark]`} {...register("date")} />
        </Field>
        <Field label="Preferred pickup time" error={errors.time?.message} htmlFor="time">
          <input id="time" data-testid="input-time" type="time" className={`${inputCls} [color-scheme:dark]`} {...register("time")} />
        </Field>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Number of passengers" error={errors.passengers?.message} htmlFor="passengers">
          <select id="passengers" data-testid="input-passengers" className={`${inputCls} bg-[#050505]`} {...register("passengers")}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={String(n)}>{n}</option>
            ))}
          </select>
        </Field>
        <Field label="Luggage" error={errors.luggage?.message} htmlFor="luggage">
          <select id="luggage" data-testid="input-luggage" className={`${inputCls} bg-[#050505]`} {...register("luggage")}>
            <option>Hand luggage only</option>
            <option>1 suitcase</option>
            <option>2 suitcases</option>
            <option>3+ suitcases or bulky items</option>
          </select>
        </Field>
      </div>

      <Field label="Passenger name" error={errors.name?.message} htmlFor="name">
        <input id="name" data-testid="input-name" autoComplete="name" className={inputCls} placeholder="Your full name" {...register("name")} />
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Mobile number" error={errors.phone?.message} htmlFor="phone">
          <input id="phone" data-testid="input-phone" type="tel" autoComplete="tel" className={inputCls} placeholder="07xxx xxxxxx" {...register("phone")} />
        </Field>
        <Field label="Email address" error={errors.email?.message} htmlFor="email">
          <input id="email" data-testid="input-email" type="email" autoComplete="email" className={inputCls} placeholder="you@example.co.uk" {...register("email")} />
        </Field>
      </div>

      <Field label="Additional information (optional)" error={errors.notes?.message} htmlFor="notes">
        <textarea id="notes" data-testid="input-notes" rows={4} className={inputCls} placeholder="Anything the driver should know" {...register("notes")} />
      </Field>

      <div>
        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#A1A1AA]">
          <input
            type="checkbox"
            data-testid="input-privacy"
            className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]"
            {...register("privacy")}
          />
          <span>
            I have read and acknowledge the{" "}
            <a href="/privacy-policy" className="text-[#D4AF37] underline-offset-4 hover:underline">
              privacy notice
            </a>
            . My details will be used only to respond to this journey request.
          </span>
        </label>
        {errors.privacy && (
          <p role="alert" className={errCls} data-testid="error-privacy">
            {errors.privacy.message}
          </p>
        )}
      </div>

      <p className="border border-[#27272A] bg-[#111111] p-4 text-sm leading-relaxed text-[#A1A1AA]" data-testid="booking-payment-note">
        Pay your driver inside the cab at the end of the journey. Cash or card accepted.
      </p>

      {status === "error" && (
        <p role="alert" data-testid="booking-submit-error" className="border border-red-900 bg-red-950/40 p-4 text-sm text-red-300">
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        data-testid="booking-submit-button"
        disabled={status === "sending"}
        className="focus-gold w-full border border-[#D4AF37] bg-[#D4AF37] px-6 py-4 text-base font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "sending" ? "Sending your request…" : "Request my £100 journey"}
      </button>
    </form>
  );
}
